import { Form, Spin } from "antd";
import i18n from "i18next";
import { type FC, useCallback, useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router";

import { FormManager } from "../../../../../components/Form/FormManager";
import { FormLayoutType } from "../../../../../components/Form/types";
import type { FormProps } from "../../../../../components/Form/types/FormProps";
import { ResourceName } from "../../../../../core/router/config/ResourceName";
import { BASE_PATH } from "../../../../../core/router/types/routerTypes";
import { showSuccessNotification } from "../../../../../core/setup/redux/state/notifications/notificationsState";
import type { FormJson } from "../../../../../core/types/FormJsonTypes";
import { useCampaignSaveMutation } from "../../services/server/CampaignApi";
import type { CampaignEntity } from "../../types/CampaignEntity";
import campaignCreateJson from "./campaign-terminals-create.json";

export const CampaignTerminalsCreateForm: FC<FormProps> = ({ runSubmit }) => {
    const formDefinition = campaignCreateJson as FormJson;
    const [form] = Form.useForm();

    const [formData, setFormData] = useState<CampaignEntity>({} as CampaignEntity);
    const onChangeCallback = useCallback(setFormData, [setFormData]);

    const navigate = useNavigate();
    const dispatch = useDispatch();
    const [saveFormData, { isLoading: saveFormIsInProgress }] = useCampaignSaveMutation();

    // Get default values from form definition
    useEffect(() => {
        if (formDefinition?.formConfiguration) {
            const initialValues: CampaignEntity = {} as CampaignEntity;

            formDefinition.formConfiguration.tabs.forEach((tab) => {
                tab.fields.forEach((field) => {
                    const defaultValue = field.attributes?.defaultValue;
                    if (defaultValue !== undefined) {
                        (initialValues as Record<string, unknown>)[field.name] = defaultValue;
                    }
                });
            });

            setFormData(initialValues as CampaignEntity);
        }
    }, [formDefinition]);

    const onValidatedCallback = useCallback(
        async (valid: boolean) => {
            if (!valid || !formData) {
                return;
            }

            // Get fresh values from form instance to capture CSV imports and other changes
            // that may not have propagated to formData due to React batching
            const freshValues = form.getFieldsValue();
            const dataToSave = { ...formData, ...freshValues };

            const result = await saveFormData({ data: dataToSave });

            const hasError = "error" in result;
            if (!hasError) {
                dispatch(
                    showSuccessNotification({
                        text: i18n.t("common.formSaveSuccess", {
                            id: result.data.id,
                            resource: "Campaign",
                        }),
                        autoCloseDisabled: false,
                    }),
                );
                navigate(`${BASE_PATH}/${ResourceName.CAMPAIGNS}`);
            }
        },
        [saveFormData, formData, form, navigate, dispatch],
    );

    return (
        <Spin spinning={saveFormIsInProgress}>
            {formDefinition?.formConfiguration && (
                <FormManager<CampaignEntity>
                    name="CampaignTerminalsCreateForm"
                    customFormInstance={form}
                    formData={formData}
                    formLayout={FormLayoutType.TABS}
                    formDefinition={formDefinition.formConfiguration}
                    onChange={onChangeCallback}
                    shouldValidate={runSubmit}
                    onValidated={onValidatedCallback}
                />
            )}
        </Spin>
    );
};
